/**
 * LikedTabContent — paginated 3-column grid of liked-video thumbnails.
 * Web port of client-native/src/features/profile/components/LikedTabContent.tsx.
 *
 * 30/page, infinite scroll near-bottom, title overlay per tile. Empty state:
 * outline heart + "No liked videos yet" / "Double tap videos to like them" /
 * blue "Watch Videos" button → feed. Uses the track-local getLikedChapters.
 */
import { FC, useEffect, useRef } from "react";
import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { colors } from "@/ui";
import ContinueButton from "@/components/Buttons/ContinueButton";
import { callWithAuth } from "@/api/Api";
import { getLikedChapters } from "./profileApi";
import type { LikedChapterItem } from "@shared_types";

const PAGE_SIZE = 30;

function youtubeThumb(youtubeId: string): string {
  return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
}

const LikedTabContent: FC = () => {
  const nav = useNavigate();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // iOS invalidates ["likedChapters"] when the feed marks likes dirty. The web
  // feed track only updates its local observable, and the global QueryClient
  // disables refetchOnMount — so refetch whenever this tab is opened to pick up
  // likes/unlikes made in the feed.
  const query = useInfiniteQuery({
    queryKey: ["likedChapters"],
    queryFn: ({ pageParam = 1 }) =>
      callWithAuth(getLikedChapters, { page: pageParam, limit: PAGE_SIZE }),
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
    refetchOnMount: "always",
  });

  // Infinite scroll via IntersectionObserver on a bottom sentinel.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        query.hasNextPage &&
        !query.isFetchingNextPage
      ) {
        query.fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query]);

  const handleVideoPress = (chapter: LikedChapterItem) => {
    // Carry the liked chapter's lessonIds + title so the feed can splice an
    // unloaded chapter right after the current position like iOS
    // (FeedNew.tsx:341-395), instead of only repositioning when the chapter
    // happens to be in the loaded feed page.
    nav(
      `/home/feed?chapterId=${chapter.chapterId}&lessonIds=${chapter.lessonIds.join(
        ","
      )}&title=${encodeURIComponent(chapter.title)}`
    );
  };

  if (query.isLoading) {
    return (
      <div style={{ padding: 20, textAlign: "center", color: colors.muted }}>
        Loading…
      </div>
    );
  }

  const chapters = query.data?.pages.flatMap((p) => p.chapters) ?? [];

  if (chapters.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 32px",
          textAlign: "center",
          gap: 8,
        }}
      >
        <Heart size={64} color={colors.greyLight} style={{ marginBottom: 8 }} />
        <span className="text-lg font-semibold" style={{ color: colors.secondary }}>
          No liked videos yet
        </span>
        <span className="text-base" style={{ color: colors.muted }}>
          Double tap videos to like them
        </span>
        <div style={{ marginTop: 16, width: "100%", maxWidth: 240 }}>
          <ContinueButton text="Watch Videos" onClick={() => nav("/home/feed")} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 8 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
        }}
      >
        {chapters.map((chapter) => {
          // iOS scales the baked-letterbox hqdefault thumbnail to crop the
          // black bars: 1.5 for 9:16, 1.35 for 16:9 (LikedTabContent.tsx:44-58).
          const scale =
            chapter.aspectRatio === "9:16"
              ? 1.5
              : chapter.aspectRatio === "16:9"
                ? 1.35
                : 1;
          return (
          <button
            key={chapter.chapterId}
            type="button"
            onClick={() => handleVideoPress(chapter)}
            style={{
              position: "relative",
              aspectRatio: "1 / 1.3",
              padding: 0,
              border: "none",
              cursor: "pointer",
              overflow: "hidden",
              backgroundColor: colors.greyLightest,
            }}
          >
            <img
              src={youtubeThumb(chapter.youtubeId)}
              alt={chapter.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: `scale(${scale})`,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                padding: "12px 6px 6px",
                background:
                  "linear-gradient(transparent, rgba(0,0,0,0.7))",
                textAlign: "left",
              }}
            >
              <span
                className="text-xs font-semibold"
                style={{
                  color: colors.white,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {chapter.title}
              </span>
            </div>
          </button>
          );
        })}
      </div>
      <div ref={sentinelRef} style={{ height: 1 }} />
      {query.isFetchingNextPage && (
        <div style={{ padding: 16, textAlign: "center", color: colors.muted }}>
          Loading…
        </div>
      )}
    </div>
  );
};

export default LikedTabContent;
